import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { RefreshIcon, CloseIcon, DownloadIcon } from './Icons';
import Pagination from './Pagination';
import CallButton from './CallButton';
import { downloadCsv } from './csvExport';
import { getKnownAgents, normalizeNumber, getCustomerNumber, getCallStatus, localDateStr } from './callUtils';

const DEFAULT_PAGE_SIZE = 50;
// Same reasoning as DailyOrders.js's AUTO_REFRESH_MS - stock-ins land and
// other agents mark orders done throughout the day.
const AUTO_REFRESH_MS = 10000;
// Matches RTO_REMARK_MAX_LENGTH in server/ivr/routes.js.
const REMARK_MAX_LENGTH = 500;

const PAYMENT_LABELS = {
  partially_paid: { label: 'Partial Paid', className: 'attempted' },
  pending: { label: 'COD Pending', className: 'critical' },
};

// Same rule as normalizeRtoOrderNumber in server/ivr/db.js - the ERP's
// order_number may or may not carry a leading '#'.
const normalizeOrderNumber = (orderNumber) => String(orderNumber ?? '').trim().replace(/^#+/, '').trim();

// stock_in_date / rto_date are bare YYYY-MM-DD dates. Parsed as local
// midnight, not UTC, same reasoning as DateRangePicker's parseLocalDate -
// otherwise a call placed early on the stock-in day would count as "before".
const parseLocalDate = (dateStr) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '');
  if (!match) return NaN;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
};

const formatDate = (dateStr) => {
  const time = parseLocalDate(dateStr);
  if (Number.isNaN(time)) return dateStr || '-';
  return new Date(time).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// FIFO by stock-in date (oldest first) - the parcel that's been sitting in
// the warehouse longest is called first. Ties fall back to order_date;
// missing dates sort to the end, same as DailyOrders.js's sortOrdersFifo.
const sortOrdersFifo = (list) => {
  const compareTimes = (aTime, bTime) => {
    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
    if (Number.isNaN(aTime)) return 1;
    if (Number.isNaN(bTime)) return -1;
    return aTime - bTime;
  };
  return [...list].sort(
    (a, b) =>
      compareTimes(parseLocalDate(a.stock_in_date), parseLocalDate(b.stock_in_date)) ||
      compareTimes(
        a.order_date ? new Date(a.order_date).getTime() : NaN,
        b.order_date ? new Date(b.order_date).getTime() : NaN
      )
  );
};

// "Today" when the picked date is today, else a short "22 Aug" label - same
// helper as DailyOrders.js's formatActionCardDate.
const formatActionCardDate = (dateStr) => {
  if (dateStr === localDateStr(new Date())) return 'Today';
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const hasPhone = (order) => Boolean(String(order.mobile_number || '').trim());

// Click-to-call attempts grouped by customer number, built once per `calls`
// change - same as AbandonedCarts.js's buildClickAttemptIndex.
const buildClickAttemptIndex = (calls) => {
  const index = new Map();
  calls.forEach((call) => {
    if (!(call.direction || '').toLowerCase().includes('click')) return;
    const number = getCustomerNumber(call);
    if (!index.has(number)) index.set(number, []);
    index.get(number).push(call);
  });
  return index;
};

// Same 3-bucket split as AbandonedCarts.js's getCartCallBucket, counting
// only click-to-call attempts made on/after the stock-in day - a call about
// this order before the parcel came back (e.g. the original COD
// confirmation) says nothing about collecting the money still owed.
const getOrderCallBucket = (order, clickAttemptsByNumber) => {
  const stockedInAt = parseLocalDate(order.stock_in_date);
  const attempts = (clickAttemptsByNumber.get(normalizeNumber(order.mobile_number)) || []).filter((call) => {
    if (Number.isNaN(stockedInAt)) return true;
    const calledAt = new Date((call.start_stamp || '').replace(' ', 'T')).getTime();
    return Number.isNaN(calledAt) || calledAt >= stockedInAt;
  });
  if (attempts.some((call) => getCallStatus(call) === 'Answered')) return 'Answered';
  if (attempts.length > 0) return 'NotReceived';
  return 'NotCalled';
};

const formatPrice = (amount) => {
  const value = Number(amount);
  if (amount === null || amount === undefined || amount === '' || Number.isNaN(value)) return '-';
  return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const productsText = (products) => (products || []).map((p) => `${p.name} x ${p.qty ?? '-'}`).join('; ');

// Confirm step for Mark Done, with an optional remark (e.g. "paid via UPI",
// "customer refused, closing").
function MarkDoneModal({ order, onClose, onSubmit }) {
  const [remark, setRemark] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(remark.trim());
    } catch (err) {
      setError(err.message || 'Failed to mark this order done.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center" onClick={onClose}>
      <div className="modal-panel modal-panel-narrow" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Mark order {order.order_number} done?</h2>
            <p className="modal-subtitle">
              {order.name || order.mobile_number || 'Unknown customer'} · {formatPrice(order.amount_due)} due
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <label className="login-field">
            <span>Remark (optional)</span>
            <textarea
              rows={3}
              value={remark}
              maxLength={REMARK_MAX_LENGTH}
              onChange={(event) => setRemark(event.target.value)}
              autoFocus
            />
          </label>
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="btn btn-primary login-submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Mark done'}
          </button>
        </form>
      </div>
    </div>
  );
}

// A call list of RTO orders the warehouse has received back into stock with
// money still owed, fetched live from the ERP (GET /erp/rto-stocked-in, see
// server/ivr/routes.js). The ERP never drops an order from its list, so the
// CRM's own Mark Done (ivr_rto_stockin_actions) is what takes one off.
function RtoStockedIn({ calls }) {
  const { authFetch } = useAuth();
  const knownAgents = useMemo(() => getKnownAgents(calls), [calls]);
  const clickAttemptsByNumber = useMemo(() => buildClickAttemptIndex(calls), [calls]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Defaults to the actual work queue, same as DailyOrders.js/AbandonedCarts.js.
  const [viewFilter, setViewFilter] = useState('NotCalled');
  const [markingOrder, setMarkingOrder] = useState(null);
  // Orders marked done from this tab. A background poll already in flight
  // when Mark Done succeeds can still return the order; this keeps it from
  // flashing back onto the list until the next poll drops it server-side.
  const doneLocallyRef = useRef(new Set());
  const [doneLog, setDoneLog] = useState([]);
  const [doneLogLoading, setDoneLogLoading] = useState(false);
  const [doneLogError, setDoneLogError] = useState('');
  const [doneLogDate, setDoneLogDate] = useState(() => localDateStr(new Date()));

  const withoutLocallyDone = (list) =>
    list.filter((order) => !doneLocallyRef.current.has(normalizeOrderNumber(order.order_number)));

  // `silent` skips the loading state for the background poll, same as
  // DailyOrders.js's fetchOrders.
  const fetchOrders = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await authFetch('/api/ivr/erp/rto-stocked-in');
      const body = await response.json();
      if (!response.ok) {
        if (!silent) setOrders([]);
        setError(body?.error || 'Failed to fetch RTO stocked-in orders from the ERP.');
        return;
      }
      setError('');
      setOrders(sortOrdersFifo(withoutLocallyDone(Array.isArray(body?.orders) ? body.orders : [])));
    } catch (err) {
      if (!silent) setOrders([]);
      setError(err.message || 'Failed to fetch RTO stocked-in orders from the ERP.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // The Done card's count is this list's length, so it's polled for the
  // picked date rather than fetched only when the card is open.
  const fetchDoneLog = async (date, { silent = false } = {}) => {
    if (!silent) {
      setDoneLogLoading(true);
      setDoneLogError('');
    }
    try {
      const response = await authFetch(`/api/ivr/erp/rto-stocked-in/actions?date=${encodeURIComponent(date)}`);
      const body = await response.json();
      if (!response.ok) {
        if (!silent) {
          setDoneLog([]);
          setDoneLogError(body?.error || 'Failed to fetch this list.');
        }
        return;
      }
      setDoneLogError('');
      setDoneLog(Array.isArray(body?.actions) ? body.actions : []);
    } catch (err) {
      if (!silent) {
        setDoneLog([]);
        setDoneLogError(err.message || 'Failed to fetch this list.');
      }
    } finally {
      if (!silent) setDoneLogLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(() => fetchOrders({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchDoneLog(doneLogDate);
    const interval = setInterval(() => fetchDoneLog(doneLogDate, { silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [doneLogDate]);

  useEffect(() => {
    setPage(1);
  }, [orders.length, viewFilter]);

  // Same "always jump back to today" behaviour as DailyOrders.js's
  // selectActionFilter.
  const selectDoneFilter = () => {
    setDoneLogDate(localDateStr(new Date()));
    setViewFilter('Done');
  };

  const callableOrders = useMemo(() => orders.filter(hasPhone), [orders]);
  const noContactOrders = useMemo(() => orders.filter((order) => !hasPhone(order)), [orders]);
  const orderBuckets = useMemo(
    () => callableOrders.map((order) => getOrderCallBucket(order, clickAttemptsByNumber)),
    [callableOrders, clickAttemptsByNumber]
  );

  const counts = useMemo(
    () => ({
      PartialPaid: callableOrders.filter((order) => order.payment_status === 'partially_paid').length,
      CodPending: callableOrders.filter((order) => order.payment_status === 'pending').length,
      NotCalled: orderBuckets.filter((bucket) => bucket === 'NotCalled').length,
      NotReceived: orderBuckets.filter((bucket) => bucket === 'NotReceived').length,
      Answered: orderBuckets.filter((bucket) => bucket === 'Answered').length,
    }),
    [callableOrders, orderBuckets]
  );

  const isDoneView = viewFilter === 'Done';
  const isBucketFilter = viewFilter === 'NotCalled' || viewFilter === 'NotReceived' || viewFilter === 'Answered';

  const filteredOrders = useMemo(() => {
    if (viewFilter === 'NoContact') return noContactOrders;
    if (viewFilter === 'PartialPaid') return callableOrders.filter((order) => order.payment_status === 'partially_paid');
    if (viewFilter === 'CodPending') return callableOrders.filter((order) => order.payment_status === 'pending');
    if (isBucketFilter) return callableOrders.filter((order, index) => orderBuckets[index] === viewFilter);
    return callableOrders;
  }, [viewFilter, isBucketFilter, callableOrders, noContactOrders, orderBuckets]);

  const activeRows = isDoneView ? doneLog : filteredOrders;
  const totalPages = Math.max(1, Math.ceil(activeRows.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageRows = activeRows.slice((page - 1) * pageSize, page * pageSize);

  // Exports exactly what the current card is showing, same as
  // IncomingCalls.js's handleExportCsv.
  const handleExportCsv = () => {
    const today = localDateStr(new Date());
    if (isDoneView) {
      downloadCsv(
        `rto-stocked-in-done-${doneLogDate}.csv`,
        ['Order Number', 'Remark', 'Agent', 'Done At'],
        doneLog.map((row) => [
          row.order_number,
          row.remark || '',
          row.agent || '',
          row.created_at ? new Date(row.created_at).toLocaleString('en-IN') : '',
        ])
      );
      return;
    }
    downloadCsv(
      `rto-stocked-in-${today}.csv`,
      ['Order Number', 'Customer', 'Mobile', 'Products', 'Total', 'Amount Due', 'Payment', 'RTO Date', 'Stock-in Date', 'Condition'],
      filteredOrders.map((order) => [
        order.order_number,
        order.name || '',
        order.mobile_number || '',
        productsText(order.products),
        order.total_price,
        order.amount_due,
        PAYMENT_LABELS[order.payment_status]?.label || order.payment_status || '',
        order.rto_date || '',
        order.stock_in_date || '',
        order.stock_in_status || '',
      ])
    );
  };

  // Fail closed like DailyOrders.js's handleCodAction: the row is removed
  // only once the server has recorded the action.
  const handleMarkDone = async (order, remark) => {
    const response = await authFetch(
      `/api/ivr/erp/rto-stocked-in/${encodeURIComponent(normalizeOrderNumber(order.order_number))}/done`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remark }),
      }
    );
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.error || 'Failed to mark this order done.');
    const key = normalizeOrderNumber(order.order_number);
    doneLocallyRef.current.add(key);
    setOrders((prev) => prev.filter((o) => normalizeOrderNumber(o.order_number) !== key));
    setMarkingOrder(null);
    fetchDoneLog(doneLogDate, { silent: true });
  };

  const renderProducts = (products) =>
    (products || []).length
      ? products.map((p, i) => (
          <div key={i}>
            {p.name} <span className="table-cell-muted">× {p.qty ?? '-'}</span>
          </div>
        ))
      : '-';

  const renderPaymentBadge = (paymentStatus) => {
    const info = PAYMENT_LABELS[paymentStatus];
    if (!info) return paymentStatus || '-';
    return <span className={`status ${info.className}`}>{info.label}</span>;
  };

  return (
    <>
      <div className="incoming-filters-bar">
        <button type="button" className="btn btn-secondary" onClick={() => fetchOrders()} disabled={loading}>
          <RefreshIcon width={14} height={14} />
          Refresh
        </button>
        <button
          type="button"
          className="btn btn-secondary export-csv-btn"
          onClick={handleExportCsv}
          disabled={activeRows.length === 0}
        >
          <DownloadIcon width={14} height={14} />
          Export CSV
        </button>
      </div>

      <div className="stat-cards">
        <button
          type="button"
          className={`stat-card ${viewFilter === 'All' ? 'active' : ''}`}
          onClick={() => setViewFilter('All')}
        >
          <div className="stat-value">{callableOrders.length}</div>
          <div className="stat-label">Total</div>
        </button>
        <button
          type="button"
          className={`stat-card attempted ${viewFilter === 'PartialPaid' ? 'active' : ''}`}
          onClick={() => setViewFilter('PartialPaid')}
        >
          <div className="stat-value">{counts.PartialPaid}</div>
          <div className="stat-label">Partial Paid</div>
        </button>
        <button
          type="button"
          className={`stat-card critical ${viewFilter === 'CodPending' ? 'active' : ''}`}
          onClick={() => setViewFilter('CodPending')}
        >
          <div className="stat-value">{counts.CodPending}</div>
          <div className="stat-label">COD Pending</div>
        </button>
        <button
          type="button"
          className={`stat-card ${viewFilter === 'NotCalled' ? 'active' : ''}`}
          title="No click-to-call since the stock-in date"
          onClick={() => setViewFilter('NotCalled')}
        >
          <div className="stat-value">{counts.NotCalled}</div>
          <div className="stat-label">Not Called</div>
        </button>
        <button
          type="button"
          className={`stat-card critical ${viewFilter === 'NotReceived' ? 'active' : ''}`}
          onClick={() => setViewFilter('NotReceived')}
        >
          <div className="stat-value">{counts.NotReceived}</div>
          <div className="stat-label">Not Received</div>
        </button>
        <button
          type="button"
          className={`stat-card answered ${viewFilter === 'Answered' ? 'active' : ''}`}
          onClick={() => setViewFilter('Answered')}
        >
          <div className="stat-value">{counts.Answered}</div>
          <div className="stat-label">Answered</div>
        </button>
        <button
          type="button"
          className={`stat-card answered ${isDoneView ? 'active' : ''}`}
          title={`Orders marked done on ${formatActionCardDate(doneLogDate)} - click to see the list`}
          onClick={selectDoneFilter}
        >
          <div className="stat-value">{doneLog.length}</div>
          <div className="stat-label">
            Done <span className="stat-label-date">({formatActionCardDate(doneLogDate)})</span>
          </div>
        </button>
        <button
          type="button"
          className={`stat-card critical ${viewFilter === 'NoContact' ? 'active' : ''}`}
          title="Orders with no mobile number in the ERP - nothing to call"
          onClick={() => setViewFilter('NoContact')}
        >
          <div className="stat-value">{noContactOrders.length}</div>
          <div className="stat-label">No Contact</div>
        </button>
      </div>

      {isDoneView && (
        <div className="incoming-filters-bar">
          <label htmlFor="rto-done-log-date">Done on</label>
          <input
            id="rto-done-log-date"
            type="date"
            className="date-filter-input"
            value={doneLogDate}
            max={localDateStr(new Date())}
            onChange={(e) => setDoneLogDate(e.target.value)}
          />
        </div>
      )}

      {isDoneView
        ? doneLogError && <div className="call-feedback call-feedback-error">{doneLogError}</div>
        : error && <div className="call-feedback call-feedback-error">{error}</div>}

      {isDoneView ? (
        doneLogLoading ? (
          <div className="loading-state">Loading…</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order Number</th>
                  <th>Remark</th>
                  <th>Agent</th>
                  <th>Date/Time</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center' }}>
                      {doneLogError ? 'Could not load this list.' : `No orders marked done on ${doneLogDate}.`}
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row, index) => (
                    <tr key={`${row.order_number}-${row.created_at}-${index}`}>
                      <td className="table-cell-strong">{row.order_number || '-'}</td>
                      <td>{row.remark || '-'}</td>
                      <td>{row.agent || '-'}</td>
                      <td>{row.created_at ? new Date(row.created_at).toLocaleString('en-IN') : '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )
      ) : loading ? (
        <div className="loading-state">Loading…</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Mobile</th>
                <th>Products</th>
                <th>Total</th>
                <th>Amount Due</th>
                <th>Payment</th>
                <th>RTO Date</th>
                <th>Stock-in Date</th>
                <th>Condition</th>
                <th>Call</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="12" style={{ textAlign: 'center' }}>
                    {error
                      ? 'Could not load RTO stocked-in orders from the ERP.'
                      : viewFilter === 'NoContact'
                      ? 'Every RTO stocked-in order has a mobile number.'
                      : orders.length === 0
                      ? 'No RTO stocked-in orders need a call right now.'
                      : 'No orders match this filter.'}
                  </td>
                </tr>
              ) : (
                pageRows.map((order, index) => (
                  <tr key={order.order_number || `${order.mobile_number}-${order.name}-${index}`}>
                    <td className="table-cell-strong">{order.order_number || '-'}</td>
                    <td>{order.name || '-'}</td>
                    <td>{order.mobile_number || '-'}</td>
                    <td>{renderProducts(order.products)}</td>
                    <td>{formatPrice(order.total_price)}</td>
                    <td className="table-cell-strong">{formatPrice(order.amount_due)}</td>
                    <td>{renderPaymentBadge(order.payment_status)}</td>
                    <td>{formatDate(order.rto_date)}</td>
                    <td>{formatDate(order.stock_in_date)}</td>
                    <td>{order.stock_in_status || '-'}</td>
                    <td>
                      {hasPhone(order) ? (
                        <CallButton
                          agents={knownAgents}
                          customerNumber={normalizeNumber(order.mobile_number)}
                          onCallTriggered={() => {}}
                        />
                      ) : (
                        <span title="No mobile number in the ERP for this order">-</span>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-primary btn-small"
                        disabled={!normalizeOrderNumber(order.order_number)}
                        title={normalizeOrderNumber(order.order_number) ? undefined : 'This order has no order number'}
                        onClick={() => setMarkingOrder(order)}
                      >
                        Mark Done
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={activeRows.length}
      />

      {markingOrder && (
        <MarkDoneModal
          order={markingOrder}
          onClose={() => setMarkingOrder(null)}
          onSubmit={(remark) => handleMarkDone(markingOrder, remark)}
        />
      )}
    </>
  );
}

export default RtoStockedIn;
